import {
  Component,
  h,
  Prop,
  ComponentInterface,
  Event,
  EventEmitter,
  Method,
  Host,
  Element,
} from "@stencil/core";

export interface IFormFieldData {
  name: string;
  value: FormFieldValue;
  isValid: boolean;
  validity: ValidityState;
}

export type FormFieldValue =
  | string
  | number
  | boolean
  | FileList
  | Array<any>
  | null;

export interface ICustomInput {
  validate(): Promise<IFormFieldData>;
}

@Component({
  tag: "signet-checkbox",
  styleUrl: "signet-checkbox.scss",
})
export class SignetCheckbox implements ComponentInterface, ICustomInput {
  @Element() el!: HTMLElement;

  checkboxId = `checkbox_${checkboxIds++}`;
  $checkbox: HTMLInputElement;

  /**
   * The text label displayed next to the checkbox
   */
  @Prop() label: string;
  /**
   * Indicates whether the checkbox is checked.
   */
  @Prop({ mutable: true, reflect: true }) checked = false;
  /**
   * Indicates whether the checkbox is required.
   */
  @Prop() required: boolean;
  /**
   * Text displayed for accessibility purposes when the checkbox is required.
   */
  @Prop() requiredText = "Required";
  /**
   * The name attribute of the checkbox input element.
   */
  @Prop() name: string;
  /**
   * Indicates whether the checkbox is disabled.
   */
  @Prop() disabled = false;

  @Prop() value: string;

  /**
   * The color of the required asterisk in the label. Defaults to 'red'.
   */
  @Prop() requiredAsteriskColor = 'red';

  @Event() changed!: EventEmitter<IFormFieldData>;

  @Method()
  async validate() {
    return this.validateField();
  }

  private changeHandler() {
    this.$checkbox.indeterminate = false;
    const detail = this.validateField();

    this.changed.emit(detail);

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: this.checked
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: this.checked
    }));
  }

  private validateField() {
    this.checked = this.$checkbox.checked;

    const fieldData: IFormFieldData = {
      name: this.name,
      value: this.checked,
      isValid: this.$checkbox.checkValidity(),
      validity: this.$checkbox.validity,
    };

    return fieldData;
  }

  render() {
    const props = {
      id: this.checkboxId,
      checked: this.checked,
      required: this.required,
    };

    return (
      <Host class="signet-checkbox">
        <input
          id={this.checkboxId}
          name={this.checkboxId}
          class="checkbox-input"
          type="checkbox"
          {...props}
          onChange={() => this.changeHandler()}
          disabled={this.disabled}
          ref={(el) => (this.$checkbox = el)}
          value={this.value}
          checked={this.checked}
        />
        <label class="checkbox-label" htmlFor={this.checkboxId}>
          <span class="checkbox-description">
            {this.label}
            {this.required && (
              <span
                class="required"
                title={this.requiredText}
                aria-hidden="true"
                style={{ color: this.requiredAsteriskColor, marginLeft: '2px' }}
              >
                *
              </span>
            )}
          </span>
        </label>
      </Host>
    );
  }
}

let checkboxIds = 0;
